import React, { useState, useRef } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
  Button,
  Chip,
  alpha,
  Switch,
  Paper,
  styled,
} from '@mui/material';
import { motion, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FAQSection from '../components/FAQ';
import { P } from '../theme/palette';

// SectionBadge pill component
const SectionBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 16px',
  borderRadius: 50,
  background: alpha('#f97316', 0.08),
  border: `1px solid ${alpha('#f97316', 0.15)}`,
  marginBottom: 24,
});

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      delay: i * 0.1,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const PricingSection = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isAnnual, setIsAnnual] = useState(true);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  const plans = [
    {
      name: 'Başlangıç',
      priceMonthly: 299,
      priceAnnual: 2990,
      description: 'Küçük dernekler için ideal başlangıç paketi',
      color: '#3b82f6',
      features: [
        { text: '5 Widget', available: true },
        { text: '1 Kullanıcı', available: true },
        { text: 'Temel Tema Özelleştirme', available: true },
        { text: 'E-posta Desteği', available: true },
        { text: 'Mobil Uygulama Erişimi', available: true },
        { text: 'Gelişmiş Analitik', available: false },
        { text: 'Özel Alan Adı', available: false },
      ],
      popular: false,
    },
    {
      name: 'Profesyonel',
      priceMonthly: 599,
      priceAnnual: 5990,
      description: 'Büyüyen dernekler için tam özellikli çözüm',
      color: '#f97316',
      features: [
        { text: 'Sınırsız Widget', available: true },
        { text: '10 Kullanıcı', available: true },
        { text: 'Gelişmiş Tema Özelleştirme', available: true },
        { text: 'Öncelikli E-posta Desteği', available: true },
        { text: 'Mobil Uygulama + Push Bildirim', available: true },
        { text: 'Gelişmiş Analitik', available: true },
        { text: 'Özel Alan Adı', available: true },
      ],
      popular: true,
    },
    {
      name: 'Kurumsal',
      priceMonthly: 1299,
      priceAnnual: 12990,
      description: 'Büyük dernekler ve federasyonlar için',
      color: '#8b5cf6',
      features: [
        { text: 'Sınırsız Widget + Özel Geliştirme', available: true },
        { text: 'Sınırsız Kullanıcı', available: true },
        { text: 'Tam Marka Özelleştirme', available: true },
        { text: '7/24 Telefon & E-posta Desteği', available: true },
        { text: 'Premium Mobil Uygulama', available: true },
        { text: 'Gerçek Zamanlı Analitik', available: true },
        { text: 'Özel Alan Adı + SSL', available: true },
      ],
      popular: false,
    },
  ];

  return (
    <Box ref={ref} sx={{ py: { xs: 10, md: 14 }, backgroundColor: '#ffffff' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            <SectionBadge>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: '#f97316',
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: '#f97316',
                  letterSpacing: '0.5px',
                }}
              >
                FİYATLANDIRMA
              </Typography>
            </SectionBadge>
          </motion.div>

          <motion.div
            variants={fadeUp}
            custom={1}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: isMobile ? '2rem' : '2.5rem',
                fontWeight: 700,
                mb: 2,
                color: P.neutral[800],
                letterSpacing: '-0.02em',
              }}
            >
              İhtiyacınıza Göre{' '}
              <Box component="span" sx={{ color: '#f97316' }}>Paket Seçin</Box>
            </Typography>
          </motion.div>

          <motion.div
            variants={fadeUp}
            custom={2}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            <Typography
              sx={{
                maxWidth: 550,
                mx: 'auto',
                color: P.neutral[500],
                lineHeight: 1.7,
                fontSize: '1rem',
                mb: 4,
              }}
            >
              Esnek fiyatlandırma ile derneğinizin büyüklüğüne uygun çözümü seçin.
            </Typography>
          </motion.div>

          {/* Toggle */}
          <motion.div
            variants={fadeUp}
            custom={3}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            <Box 
              sx={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: 1.5,
                p: 0.5,
                borderRadius: 50,
                background: P.neutral[100],
                border: `1px solid ${P.neutral[200]}`,
              }}
            >
              <Box
                onClick={() => setIsAnnual(false)}
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: 50,
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  color: !isAnnual ? '#fff' : P.neutral[600],
                  background: !isAnnual ? '#f97316' : 'transparent',
                  transition: 'all 0.3s ease',
                }}
              >
                Aylık
              </Box>
              <Box
                onClick={() => setIsAnnual(true)}
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: 50,
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  color: isAnnual ? '#fff' : P.neutral[600],
                  background: isAnnual ? '#f97316' : 'transparent',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                Yıllık
                <Chip
                  label="-20%"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    background: isAnnual ? 'rgba(255,255,255,0.25)' : alpha('#f97316', 0.15),
                    color: isAnnual ? '#fff' : '#f97316',
                  }}
                />
              </Box>
            </Box>
          </motion.div>
        </Box>

        {/* Pricing Cards */}
        <Grid container spacing={3} justifyContent="center">
          {plans.map((plan, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <motion.div
                variants={fadeUp}
                custom={index + 4}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
              >
                <Paper
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    backgroundColor: '#ffffff',
                    p: 3,
                    border: plan.popular 
                      ? `2px solid ${plan.color}` 
                      : `1px solid ${P.neutral[200]}`,
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: plan.popular
                        ? `0 16px 40px ${alpha(plan.color, 0.2)}`
                        : `0 12px 32px ${alpha('#000', 0.08)}`,
                      borderColor: plan.color,
                    },
                  }}
                >
                  {/* Popular Badge */}
                  {plan.popular && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 2,
                        py: 0.5,
                        borderRadius: 50,
                        background: `linear-gradient(135deg, ${plan.color} 0%, ${alpha(plan.color, 0.8)} 100%)`,
                        boxShadow: `0 4px 12px ${alpha(plan.color, 0.3)}`,
                      }}
                    >
                      <LocalOfferIcon sx={{ fontSize: 14, color: '#fff' }} />
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#fff' }}>
                        EN POPÜLER
                      </Typography>
                    </Box>
                  )}

                  {/* Plan Header */}
                  <Box sx={{ textAlign: 'center', mb: 3, pt: plan.popular ? 1 : 0 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${plan.color} 0%, ${alpha(plan.color, 0.7)} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                      }}
                    >
                      <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: '#fff' }}>
                        {plan.name.charAt(0)}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        color: P.neutral[800],
                        mb: 0.5,
                      }}
                    >
                      {plan.name}
                    </Typography>
                    <Typography
                      sx={{
                        color: P.neutral[500],
                        fontSize: '0.85rem',
                        lineHeight: 1.5,
                      }}
                    >
                      {plan.description}
                    </Typography>
                  </Box>

                  {/* Price */}
                  <Box sx={{ textAlign: 'center', mb: 3, pb: 3, borderBottom: `1px solid ${P.neutral[100]}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center' }}>
                      <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: P.neutral[500], mr: 0.5 }}>
                        ₺
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: '2.5rem',
                          color: P.neutral[800],
                          lineHeight: 1,
                        }}
                      >
                        {isAnnual
                          ? Math.round(plan.priceAnnual / 12)
                          : plan.priceMonthly}
                      </Typography>
                      <Typography sx={{ fontSize: '0.9rem', color: P.neutral[500], ml: 0.5 }}>
                        /ay
                      </Typography>
                    </Box>
                    {isAnnual && (
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          color: plan.color,
                          fontWeight: 600,
                          mt: 1,
                        }}
                      >
                        Yıllık ₺{plan.priceAnnual.toLocaleString()} • 2 ay bedava
                      </Typography>
                    )}
                  </Box>

                  {/* Features */}
                  <Box sx={{ mb: 3 }}>
                    {plan.features.map((feature, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 1.5,
                          opacity: feature.available ? 1 : 0.5,
                        }}
                      >
                        {feature.available ? (
                          <CheckCircleOutlineIcon 
                            sx={{ 
                              fontSize: 18, 
                              mr: 1.5, 
                              color: '#10b981',
                            }} 
                          />
                        ) : (
                          <RemoveCircleOutlineIcon 
                            sx={{ 
                              fontSize: 18, 
                              mr: 1.5, 
                              color: P.neutral[400],
                            }} 
                          />
                        )}
                        <Typography 
                          sx={{ 
                            fontSize: '0.85rem',
                            color: feature.available ? P.neutral[700] : P.neutral[400],
                            fontWeight: 500,
                          }}
                        >
                          {feature.text}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* CTA Button */}
                  <Button
                    fullWidth
                    variant={plan.popular ? 'contained' : 'outlined'}
                    size="large"
                    endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
                    onClick={() => navigate('/basvuru')}
                    sx={{
                      py: 1.5,
                      fontWeight: 600,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '0.95rem',
                      background: plan.popular 
                        ? `linear-gradient(135deg, ${plan.color} 0%, ${alpha(plan.color, 0.85)} 100%)`
                        : 'transparent',
                      color: plan.popular ? 'white' : plan.color,
                      borderColor: plan.color,
                      borderWidth: 1.5,
                      boxShadow: plan.popular ? `0 4px 16px ${alpha(plan.color, 0.3)}` : 'none',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: plan.popular 
                          ? `linear-gradient(135deg, ${alpha(plan.color, 0.9)} 0%, ${plan.color} 100%)`
                          : alpha(plan.color, 0.08),
                        borderColor: plan.color,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 6px 20px ${alpha(plan.color, 0.25)}`,
                      },
                    }}
                  >
                    {plan.popular ? 'Hemen Başla' : 'Paketi İncele'}
                  </Button>
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        <FAQSection />
      </Container>
    </Box>
  );
};

export default PricingSection;